<template>
  <div v-if="supported" class="turn-bell">
    <button type="button" class="secondary turn-bell__button" :disabled="busy" @click="toggle">
      {{ on ? 'Turn notifications are on — turn off' : 'Tell me when it’s my turn' }}
    </button>
    <p v-if="note" class="turn-bell__note">{{ note }}</p>
  </div>
</template>

<script>
// "Tell me when it's my turn" (2026-10-04, Matt: "I'll want notifications
// for all of these").
//
// HOUSE MODULE: byte-identical in every Around Table Round game. A push
// subscription belongs to ONE origin, and on iOS only to an app on the Home
// Screen, so each game offers its own bell; the subscription is saved at
// atrPush/<uid>/subscriptions/<id> tagged with this game's room prefix, and
// the hub's atr-push Lambda sends "Your turn in <this game>" to it — so the
// tap opens this game. The hub's own bell (games.js) is the fallback for a
// game with no subscription of its own.
//
// public/push-sw.js is registered on its own scope so it never replaces the
// game's real service worker.
import { ref as dbRef, set } from 'firebase/database';
import { db } from '@/firebase';

// The atr-push Lambda's VAPID public key — public by design, same as the hub's.
const VAPID_PUBLIC_KEY = 'BPxOUP62DQBAJXpY2Zw2W01rbSAotteS1LH0gVCkAfVQsbzdFrqpVRAN6BSZDXbZht710R1VbyC-wNYLDQ7V0ng';
const SW_URL = '/push-sw.js';
const SW_SCOPE = '/push/';

const pushSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const subscriptionId = (endpoint) => {
  let hash = 0;
  for (let i = 0; i < endpoint.length; i += 1) hash = ((hash << 5) - hash + endpoint.charCodeAt(i)) | 0;
  return `sub-${Math.abs(hash).toString(36)}`;
};

export default {
  name: 'TurnBell',
  props: { game: { type: String, required: true } },
  data() { return { supported: pushSupported(), on: false, busy: false, note: '' }; },
  computed: { uid() { return this.$store.state.user?.uid || null; } },
  async mounted() {
    await this.refresh();
    // Opening or foregrounding the game closes the turn bells still sitting
    // in Notification Center — iOS never does on its own, so they piled up
    // long after the turn was taken. The badge is handled the same way.
    this.closeDelivered();
    document.addEventListener('visibilitychange', this.onVisible);
  },
  beforeUnmount() { document.removeEventListener('visibilitychange', this.onVisible); },
  methods: {
    onVisible() { if (document.visibilityState === 'visible') this.closeDelivered(); },
    async closeDelivered() {
      if (!this.supported) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
        for (const n of (await reg?.getNotifications?.()) || []) n.close();
        await navigator.clearAppBadge?.();
      } catch { /* they stay until swiped */ }
    },
    async registration() {
      return (await navigator.serviceWorker.getRegistration(SW_SCOPE)) || navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
    },
    async current() {
      try { return (await (await this.registration()).pushManager.getSubscription()) || null; } catch { return null; }
    },
    async refresh() { this.on = Boolean(await this.current()); },
    async toggle() {
      if (!this.uid || !db) { this.note = 'Sign in first.'; return; }
      this.busy = true; this.note = '';
      try {
        if (this.on) await this.unsubscribe(); else await this.subscribe();
      } catch (e) { this.note = e.message; } finally { this.busy = false; await this.refresh(); }
    },
    async subscribe() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
      if (isIOS && !standalone) throw new Error('On iOS, add this game to your Home Screen (from Safari) first — notifications only work in the installed app.');
      if ((await Notification.requestPermission()) !== 'granted') throw new Error('Notifications are blocked in this browser’s settings.');
      const reg = await this.registration();
      await navigator.serviceWorker.ready.catch(() => {});
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
      const json = sub.toJSON();
      await set(dbRef(db, `atrPush/${this.uid}/subscriptions/${subscriptionId(json.endpoint)}`), {
        endpoint: json.endpoint, keys: json.keys, ua: navigator.userAgent, savedAt: Date.now(), game: this.game,
      });
      this.note = 'On. A test notification is on its way.';
    },
    async unsubscribe() {
      const sub = await this.current();
      if (!sub) return;
      const id = subscriptionId(sub.endpoint);
      await sub.unsubscribe().catch(() => {});
      await set(dbRef(db, `atrPush/${this.uid}/subscriptions/${id}`), null);
    },
  },
};
</script>

<style scoped>
.turn-bell { display: flex; flex-direction: column; gap: 0.3rem; margin: 0.25rem 0 0.5rem; }
.turn-bell__button { font-size: 0.9rem; }
.turn-bell__note { margin: 0; font-size: 0.85rem; opacity: 0.8; }
</style>
